// Logique pure de repartition en % du volume de commande par departement.
// Modele "ligne tampon" : la DERNIERE entree de l'ordre d'affichage absorbe le reliquat,
// si bien que chaque annee totalise toujours exactement 100 %. Aucune regle d'arrondi
// complexe necessaire : le tampon porte le reste.

/** Arrondi a 2 decimales, stable face au bruit flottant. */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Valeur de la ligne tampon = 100 - somme des autres, bornee a [0, 100].
 * Si les autres depassent deja 100, le tampon vaut 0 (le total affichera > 100, signale en rouge,
 * et la sauvegarde est bloquee tant que l'acheteur n'a pas ramene le total a 100).
 */
export function tampon(autres: number[]): number {
  const somme = autres.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
  return round2(Math.max(0, 100 - somme));
}

/**
 * Repartit 100 % sur depIds (dans l'ordre d'affichage). La derniere entree est le tampon
 * et absorbe le reliquat -> la somme fait toujours exactement 100.
 * - poids fournis (ex. derniere annee d'historique) : repartition proportionnelle re-normalisee ;
 * - sinon, ou si tous les poids sont nuls : repartition equitable.
 */
export function repartir(depIds: number[], poids?: Record<number, number>): Record<number, number> {
  const out: Record<number, number> = {};
  const n = depIds.length;
  if (n === 0) {
    return out;
  }
  if (n === 1) {
    out[depIds[0]] = 100;
    return out;
  }

  const brut = depIds.map((id) => (poids ? Math.max(0, poids[id] ?? 0) : 1));
  const total = brut.reduce((s, w) => s + w, 0);
  const ref = total > 0 ? brut : depIds.map(() => 1); // poids tous nuls -> equitable
  const refTotal = ref.reduce((s, w) => s + w, 0);

  let acc = 0;
  for (let i = 0; i < n - 1; i++) {
    const pct = round2((ref[i] / refTotal) * 100);
    out[depIds[i]] = pct;
    acc += pct;
  }
  out[depIds[n - 1]] = round2(100 - acc); // tampon

  return out;
}
